import { DateTimeISO, Money, PaginatedResponse } from './common';
import { TransactionPurpose, TransactionType } from './transactionRequests';

export enum TREASURY_PRIME_CHECK_DEPOSIT_ERROR_REASONS {
  UNKNOWN = 'unknown',
  EXCEEDS_ACH_LIMIT = 'exceeds_ach_limit',
  EXCEEDS_PROGRAM_LIMIT = 'exceeds_program_limit',
  INSUFFICIENT_FUNDS = 'insufficient_funds',
  EXCEEDS_TRANSACTION_LIMIT = 'exceeds_transaction_limit',
  EXCEEDS_ACCOUNT_LIMIT = 'exceeds_account_limit',
  CANCELED_OCR = 'canceled_ocr',
  DEPOSIT_CHECK_CANCELED = 'canceled',
  DEPOSIT_CHECK_RETURNED = 'returned',
  DEPOSIT_CHECK_ERROR = 'error',
}

export enum OwnershipType {
  Individual = 'individual',
  Business = 'business',
  Joint = 'joint',
}

export enum TransactionStatusCategory {
  PENDING = 'pending',
  VOIDED = 'voided',
  PROCESSED = 'processed',
}

export enum TransactionStatusLabel {
  PENDING = 'Pending',
  CANCELED = 'Canceled',
  ERROR = 'Error',
  SETTLED = 'Settled',
  RETURNED = 'Returned',
  IMAGE_ERROR = 'Image Error',
  STOP_REQUESTED = 'Stop Requested',
  STOPPED = 'Stopped',
  CLEARED = 'Cleared',
  EXPIRED = 'Expired',
  VOIDED = 'Voided',
  FAILED = 'Failed',
}

export enum TransferType {
  ACH = 'ach',
  Book = 'book',
  Check = 'check',
  SameDayACH = 'same-day-ach',
  Wire = 'wire',
  Standard = 'standard',
}

export type AccountOrigins =
  | 'internal'
  | 'external'
  | 'third party wire recipient'
  | 'payee';

export enum TRANSACTION_TYPE {
  Withdrawal,
  Deposit,
  InternalTransfer,
  Wire,
}

export enum TransactionDirection {
  Incoming = 'incoming',
  Outgoing = 'outgoing',
}

export enum TransactionInitiatedBy {
  InternalOriginated = 'internalOriginated',
  ExternalOriginated = 'externalOriginated',
  Interest = 'interest',
  Fee = 'fee',
  AdHoc = 'adHoc',
}

export type TransactionAccountOrigin =
  | 'external'
  | 'internal'
  | 'third party wire recipient'
  | 'payee';

export type TransactionAccountInfoBase = {
  bankName: string;
  id: string;
  last4: string;
  origin: TransactionAccountOrigin;
  ownershipType: OwnershipType | null;
  logoImageUrl: string | null;
  nickName: string | null;
};

type TransactionAccountInfoAddress = {
  addressCity: string;
  addressLine1: string;
  addressLine2: string;
  addressPostalCode: string;
  addressStateCode: string;
};

export type TransactionToAccountInfo = TransactionAccountInfoBase & {
  nickName: string;
  recipientAddress: TransactionAccountInfoAddress | null;
  recipientName: string | null;
};

type TransactionItemBase = {
  id: string;
  amount: Money;
  balance: Money | null;
  shortId: string;
  fromAccount: TransactionAccountInfoBase | null;
  toAccount: TransactionToAccountInfo | null;
  statusLabel: TransactionStatusLabel;
  statusCategory: TransactionStatusCategory;
  transferType: TransferType;
  direction: TransactionDirection | null;
  description: string;
  additionalDescription: string | null;
  note: string | null;
  instructions: string | null;
  settledAt: DateTimeISO | null;
  canceledAt: DateTimeISO | null;
  startedAt: DateTimeISO;
  feeForId: DateTimeISO | null;
  dateGroup: DateTimeISO | null;
  errorReason: TREASURY_PRIME_CHECK_DEPOSIT_ERROR_REASONS | null;
  purpose: string | null;
};

export type TransactionItemCheckDeposit = TransactionItemBase & {
  checkDeposit: {
    imageBackUrl: string;
    imageFrontUrl: string;
  };
  initiationType: TransactionInitiatedBy;
  issuedCheck: null;
  transferType: TransferType.Check;
};

export type TransactionItemCheckIssue = TransactionItemBase & {
  checkDeposit: null;
  initiationType: TransactionInitiatedBy;
  issuedCheck: {
    canceledAt: DateTimeISO | null;
    clearedAt: DateTimeISO | null;
    errorAt: DateTimeISO | null;
    expiredAt: DateTimeISO | null;
    isCancelable: boolean;
    isStoppable: boolean;
    issuedAt: DateTimeISO;
    paymentStoppedAt: DateTimeISO | null;
    returnedAt: DateTimeISO | null;
    sentAt: DateTimeISO | null;
    memo: string | null;
  };
  transferType: TransferType.Check;
};

export type TransactionItemStandard = TransactionItemBase & {
  checkDeposit: null;
  issuedCheck: null;
  initiationType: TransactionInitiatedBy;
  transferType: Exclude<TransferType, TransferType.Check>;
};

export type TransactionItem =
  | TransactionItemCheckDeposit
  | TransactionItemCheckIssue
  | TransactionItemStandard;

export type TransactionsListQueryParams = {
  page: number;
  status: TransactionStatusCategory;
  records_per_page?: number;
};

export type TransactionListResponse = PaginatedResponse<TransactionItem>;

export type ScheduledTransactionScheduleType = 'one time' | 'recurring';
export type ScheduledTransactionRecurringScheduleType =
  | 'monthly'
  | 'weekly'
  | 'every two weeks'
  | 'every four weeks'
  | 'every other month';
export enum ScheduledTransactionStatus {
  ACTIVE = 'active',
  PAUSED = 'paused',
  CANCELED = 'canceled',
  COMPLETED = 'completed',
}
export type ScheduledDaysOfTheMonth =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27
  | 28
  | 29
  | 30
  | 31;
export type ScheduledDaysOfTheWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday';
export type ScheduleStopSendingAfter = 'do not stop' | 'number of occurrences';
type ScheduledTransactionAccountInfo = {
  bankAccountOrigin: AccountOrigins;
  bankLogoUrl?: string;
  ownershipType: OwnershipType | null;
};

export type ScheduledTransactionItem = {
  uuid: string;
  amount: Money;
  description: string;
  displayType: string;
  status: ScheduledTransactionStatus;
  scheduleType: ScheduledTransactionScheduleType;
  recurringScheduleType: ScheduledTransactionRecurringScheduleType;
  nextOccurrenceDate: DateTimeISO;
  toAccountId: string;
  toAccount: string;
  toAccountDetails: ScheduledTransactionAccountInfo;
  fromAccountId: string;
  fromAccount: string;
  fromAccountDetails: ScheduledTransactionAccountInfo;
  scheduleDayOfMonth: ScheduledDaysOfTheMonth;
  scheduleDayOfWeek: ScheduledDaysOfTheWeek;
};

export type ScheduledTransactionDetails = {
  uuid: string;
  toAccountId: string;
  fromAccountId: string;
  fromAccount: string;
  fromAccountDetails: ScheduledTransactionAccountInfo;
  toAccount: string;
  toAccountDetails: ScheduledTransactionAccountInfo;
  status: ScheduledTransactionStatus;
  transferType: TransactionType;
  transactionType: TRANSACTION_TYPE;
  displayType: string;
  consumerId: string;
  amount: Money;
  description: string;
  note: string;
  instructions: string;
  purpose: TransactionPurpose | null;
  scheduleType: ScheduledTransactionScheduleType;
  scheduleOneTimeDate: DateTimeISO;
  recurringScheduleType: ScheduledTransactionRecurringScheduleType | null;
  scheduleDayOfMonth: ScheduledDaysOfTheMonth | null;
  scheduleDayOfWeek: ScheduledDaysOfTheWeek | null;
  scheduleStopSendingAfter: ScheduleStopSendingAfter | null;
  scheduleStopSendingAfterNumberOfOccurrences: number | null;
  nextOccurrenceDate: DateTimeISO;
  numberOfOccurrences: number | null;
  numberOfRemainingOccurrences: number | null;
};

export type ScheduledTransactionsListQueryParams = {
  page: number;
  statuses?: ScheduledTransactionStatus[];
  records_per_page?: number;
};

export type ScheduledTransactionsAggregateInfo = {
  totalActive: number;
  totalPaused: number;
  totalCanceled: number;
  totalCompleted: number;
};

export type ScheduledTransactionListResponse = {
  scheduledTransactions: PaginatedResponse<ScheduledTransactionItem>;
  aggregateInfo: ScheduledTransactionsAggregateInfo;
};

export const queryKeys = {
  list: ['transactionList'] as const,
  details: ['transcationDetails'] as const,
  scheduledList: ['scheduledTransactionList'] as const,
  scheduledDetails: ['scheduledTranscationDetails'] as const,
};
